/**
 * WaitingRoom Component
 *
 * Displayed while waiting for other players in the familiarity-based allocation system.
 * Shows allocation status and includes an optional mini-game to keep participants engaged.
 *
 * Used in two contexts:
 * 1. As a lobby (before game starts) - props come from Empirica lobby system
 * 2. During game (waiting for stage submission) - props passed explicitly
 *
 * Features:
 * - Dynamic messages based on wait time
 * - Allocation status display (assigned topic/domain when available)
 * - Optional dino game for engagement during longer waits
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { usePlayer } from "@empirica/core/player/classic/react";

// Dynamic messages based on elapsed wait time (15 minute max)
function getWaitMessage(elapsedSeconds) {
  const minutes = Math.floor(elapsedSeconds / 60);

  if (minutes < 2) {
    return "Most participants are matched quickly";
  } else if (minutes < 5) {
    return "Still looking for your partner...";
  } else if (minutes < 12) {
    return "Taking a bit longer than usual, please hang tight";
  } else {
    return "Almost at the 15-minute limit. If no partner is found, you'll be asked to submit a partial-completion code.";
  }
}

// Format seconds as M:SS
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Simple Dino Game Component
function DinoGame() {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('idle'); // idle, playing, gameOver
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('dinoHighScore') || '0');
  });

  const gameRef = useRef({
    dino: { y: 150, vy: 0, jumping: false },
    obstacles: [],
    speed: 5,
    frame: 0
  });

  const jump = useCallback(() => {
    if (gameState === 'idle') {
      setGameState('playing');
      gameRef.current = {
        dino: { y: 150, vy: 0, jumping: false },
        obstacles: [],
        speed: 5,
        frame: 0
      };
      setScore(0);
    }
    if (gameState === 'playing' && !gameRef.current.dino.jumping) {
      gameRef.current.dino.vy = -12;
      gameRef.current.dino.jumping = true;
    }
    if (gameState === 'gameOver') {
      setGameState('idle');
    }
  }, [gameState]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;

    const gameLoop = () => {
      if (gameState !== 'playing') return;

      const game = gameRef.current;
      game.frame++;

      // Update dino
      game.dino.vy += 0.6; // gravity
      game.dino.y += game.dino.vy;
      if (game.dino.y >= 150) {
        game.dino.y = 150;
        game.dino.vy = 0;
        game.dino.jumping = false;
      }

      // Spawn obstacles
      if (game.frame % 100 === 0) {
        game.obstacles.push({ x: 400, width: 20, height: 30 + Math.random() * 20 });
      }

      // Update obstacles
      game.obstacles = game.obstacles.filter(obs => {
        obs.x -= game.speed;
        return obs.x > -50;
      });

      // Check collision
      const dinoBox = { x: 50, y: game.dino.y, w: 30, h: 30 };
      for (const obs of game.obstacles) {
        const obsBox = { x: obs.x, y: 180 - obs.height, w: obs.width, h: obs.height };
        if (
          dinoBox.x < obsBox.x + obsBox.w &&
          dinoBox.x + dinoBox.w > obsBox.x &&
          dinoBox.y < obsBox.y + obsBox.h &&
          dinoBox.y + dinoBox.h > obsBox.y
        ) {
          setGameState('gameOver');
          if (game.frame > highScore) {
            setHighScore(game.frame);
            localStorage.setItem('dinoHighScore', game.frame.toString());
          }
          return;
        }
      }

      // Increase speed
      if (game.frame % 500 === 0) {
        game.speed += 0.5;
      }

      setScore(game.frame);

      // Draw
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, 400, 200);

      // Ground
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(0, 180, 400, 2);

      // Dino
      ctx.fillStyle = '#3b82f6';
      ctx.fillRect(50, game.dino.y, 30, 30);
      // Eye
      ctx.fillStyle = 'white';
      ctx.fillRect(70, game.dino.y + 5, 5, 5);

      // Obstacles
      ctx.fillStyle = '#64748b';
      for (const obs of game.obstacles) {
        ctx.fillRect(obs.x, 180 - obs.height, obs.width, obs.height);
      }

      // Score
      ctx.fillStyle = '#475569';
      ctx.font = '14px monospace';
      ctx.fillText(`Score: ${game.frame}`, 10, 20);

      animationId = requestAnimationFrame(gameLoop);
    };

    if (gameState === 'playing') {
      animationId = requestAnimationFrame(gameLoop);
    } else {
      // Draw idle/game over state
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, 400, 200);
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(0, 180, 400, 2);
      ctx.fillStyle = '#3b82f6';
      ctx.fillRect(50, 150, 30, 30);
      ctx.fillStyle = 'white';
      ctx.fillRect(70, 155, 5, 5);

      ctx.fillStyle = '#475569';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      if (gameState === 'idle') {
        ctx.fillText('Press SPACE or tap to start', 200, 100);
      } else {
        ctx.fillText(`Game Over! Score: ${score}`, 200, 90);
        ctx.fillText('Press SPACE or tap to restart', 200, 110);
      }
      ctx.textAlign = 'left';
    }

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [gameState, score, highScore]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        jump();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [jump]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-2 bg-slate-100 border-b border-slate-200 flex justify-between items-center">
        <span className="text-sm font-medium text-slate-600">Mini Game</span>
        <span className="text-xs text-slate-500">High Score: {highScore}</span>
      </div>
      <canvas
        ref={canvasRef}
        width={400}
        height={200}
        onClick={jump}
        className="cursor-pointer w-full"
        style={{ imageRendering: 'pixelated' }}
      />
    </div>
  );
}

export function WaitingRoom({ participantsReady, totalNeeded }) {
  const player = usePlayer();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showGame, setShowGame] = useState(false);

  // Get domain rankings from intro step (if available)
  const domainRankings = player?.get("domainRankings");

  // Timer for lobby waiting
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Show game option after 30 seconds
  useEffect(() => {
    if (elapsedSeconds >= 30 && !showGame) {
      // Don't auto-show, just make it available
    }
  }, [elapsedSeconds, showGame]);

  // If used as lobby (no explicit props), show lobby-specific message
  const isLobby = participantsReady === undefined && totalNeeded === undefined;

  // Default values for in-game waiting
  const ready = participantsReady ?? 1;
  const total = totalNeeded ?? 2;
  const progress = (ready / total) * 100;

  // Dynamic message based on wait time
  const waitMessage = getWaitMessage(elapsedSeconds);

  // Get top ranked domain for display
  const topDomain = domainRankings
    ? Object.entries(domainRankings)
        .sort(([, a], [, b]) => a - b)
        .map(([domain]) => domain)[0]
    : null;

  // Hard 15-minute lobby cap: if still unmatched, stop waiting and tell the
  // participant to submit the partial-completion code (partial compensation applies).
  const LOBBY_TIMEOUT_SECONDS = 900; // 15 minutes
  if (isLobby && elapsedSeconds >= LOBBY_TIMEOUT_SECONDS) {
    return (
      <div className="h-full w-full bg-gradient-to-b from-white to-gray-100 flex items-center justify-center p-8">
        <div className="max-w-lg w-full bg-white rounded-2xl border border-amber-200 shadow-sm p-8 text-center">
          <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
            <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No partner found</h1>
          <p className="text-gray-600 mb-4">
            We're sorry — we couldn't match you with a partner within 15 minutes. This study
            needs two people working together, so it can't continue on your own. You'll still be
            <strong> partially compensated for your time</strong>.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800 mb-3">
              To receive your partial payment, submit this completion code on Prolific:
            </p>
            <div className="bg-white border border-amber-300 rounded-lg py-3 px-4 font-mono text-xl font-bold text-gray-900 tracking-widest select-all">
              alice
            </div>
            <p className="text-xs text-amber-700 mt-3">
              Enter this code in your Prolific submission to complete the study and receive your partial payment.
            </p>
          </div>
          <p className="text-xs text-gray-400 mt-5">You can then safely close this tab.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-gradient-to-b from-white to-gray-100 flex items-center justify-center p-8 relative overflow-hidden">
      {/* Subtle background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse"
          style={{ backgroundColor: "rgba(59, 130, 246, 0.03)" }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse"
          style={{ backgroundColor: "rgba(59, 130, 246, 0.02)", animationDelay: "1s" }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-lg w-full">
        {/* Animated waiting indicator */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            {/* Outer ring animation */}
            <div
              className="absolute inset-0 w-20 h-20 rounded-full border-2 border-blue-200 opacity-50 animate-ping"
              style={{ animationDuration: "2s" }}
            />

            {/* Spinner ring */}
            <div className="w-20 h-20 rounded-full border-4 border-blue-100" />
            <div
              className="absolute inset-0 w-20 h-20 rounded-full border-4 border-transparent border-t-blue-500 animate-spin"
              style={{ animationDuration: "1.5s" }}
            />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-center mb-2 text-gray-900">
          {isLobby ? "Finding Your Partner" : "Waiting for Your Partner"}
        </h1>

        {/* Subtitle */}
        <p className="text-center text-gray-500 mb-8">
          {isLobby
            ? "Please wait while we match you with another participant."
            : "Please wait while your partner completes the current step."
          }
        </p>

        {/* Status card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
          {/* Progress section - only show counts if we have them (in-game waiting) */}
          {!isLobby && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-600">
                  Players ready
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {ready} / {total}
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Lobby-specific message with elapsed time */}
          {isLobby && (
            <div className="mb-6">
              {/* Elapsed time display */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-600">
                  Time waiting
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium font-mono">
                  {formatTime(elapsedSeconds)}
                </span>
              </div>

              {/* Dynamic message */}
              <p className="text-gray-600 text-center">
                {waitMessage}
              </p>

              {/* Allocation info if rankings are available */}
              {domainRankings && topDomain && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700 text-center">
                    <span className="font-medium">Your preferences recorded!</span>
                    <br />
                    <span className="text-xs">You'll be matched to a topic based on your rankings.</span>
                  </p>
                </div>
              )}

              {/* Max time hint and guidance */}
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-xs text-gray-500 text-center">
                  <span className="font-medium">Maximum wait: 15 minutes</span>
                  <br />
                  <span className="mt-1 block">
                    If no partner is found, we kindly ask you to return your submission on Prolific
                    so you can participate in another study without losing time.
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Info items */}
          <div className="space-y-3">
            <InfoItem
              icon={<ClockIcon />}
              text="Please remain on this page"
            />
            <InfoItem
              icon={<UsersIcon />}
              text={isLobby ? "You'll continue once matched with a partner" : "You'll continue once your partner is ready"}
            />
            <InfoItem
              icon={<ShieldIcon />}
              text="Your responses are anonymous"
              iconColor="text-green-500"
            />
          </div>
        </div>

        {/* Mini game section (shows after 30 seconds) */}
        {isLobby && elapsedSeconds >= 30 && (
          <div className="mb-6">
            {!showGame ? (
              <button
                onClick={() => setShowGame(true)}
                className="w-full py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border border-slate-200 transition-colors text-sm font-medium"
              >
                🎮 Play a mini-game while you wait
              </button>
            ) : (
              <div className="space-y-2">
                <DinoGame />
                <button
                  onClick={() => setShowGame(false)}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Hide game
                </button>
              </div>
            )}
          </div>
        )}

        {/* Footer status */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-green-600 text-xs font-medium">
              Connected
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoItem({ icon, text, iconColor = "text-blue-500" }) {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-600">
      <div className={`w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center ${iconColor}`}>
        {icon}
      </div>
      <span>{text}</span>
    </div>
  );
}

// Icons
function ClockIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}
